import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { DollarSign, Building2, User, Calendar, Trophy, Download, Award, HeartHandshake } from "lucide-react";
import FinanceCredenciadosPanel from "./FinanceCredenciadosPanel";
import AdminFinanceFPPanel from "./AdminFinanceFPPanel";

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; } };

const AdminFinancePanel = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [product, setProduct] = useState("raf"); // "raf" | "fp"
  const [tab, setTab] = useState("consultores");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/finance/report?days=${days}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao carregar relatório"); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const r = await api.get(`/admin/finance/report.csv?days=${days}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `raf_finance_${days}d.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado — abra na sua planilha do contábil.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao exportar CSV");
    }
  };
  if (!data) return <div className="p-6 text-slate-500 text-sm">Carregando relatório financeiro…</div>;

  // Product tabs: RAF Direct (internações + credenciados) OU Filho Presente (assinaturas)
  return (
    <div className="space-y-5" data-testid="admin-finance-panel">
      <div className="flex gap-1 border border-slate-200 rounded-lg overflow-hidden bg-white mb-2 w-fit" data-testid="finance-product-tabs">
        <button onClick={() => setProduct("raf")} data-testid="finance-product-raf"
          className={`px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 ${product === "raf" ? "bg-[#0B2B62] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <DollarSign className="w-3.5 h-3.5" /> RAF Direct
        </button>
        <button onClick={() => setProduct("fp")} data-testid="finance-product-fp"
          className={`px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 ${product === "fp" ? "bg-amber-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <HeartHandshake className="w-3.5 h-3.5" /> Filho Presente
        </button>
      </div>
      {product === "fp" && <AdminFinanceFPPanel />}
      {product === "raf" && <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-700" />
            <h2 className="font-display font-bold text-xl text-[#0B2B62]">Relatório Financeiro</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Matrículas RAF + mensalidades + comissões pagas. Base: internações confirmadas
            (clínica registra chegada → consultor confirma).
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex gap-1 text-xs">
            {[[30,"30d"],[90,"90d"],[180,"6m"],[365,"1a"]].map(([v,lbl]) => (
              <button key={v} onClick={() => setDays(v)} data-testid={`finance-range-${v}`}
                className={`px-3 py-1.5 rounded-md border ${days === v ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {lbl}
              </button>
            ))}
          </div>
          <button onClick={downloadCsv} data-testid="finance-export-csv"
            className="text-xs px-3 py-1.5 bg-emerald-700 text-white rounded-md font-semibold hover:bg-emerald-800 inline-flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BigStat label="Matrículas totais" value={brl(data.totals.matricula)} accent="emerald" />
        <BigStat label="Comissões pagas" value={brl(data.totals.commission)} accent="blue" />
        <BigStat label="Líquido RAF Direct" value={brl(data.totals.net_raf)} accent="amber" />
        <BigStat label="Internações confirmadas" value={data.total_admissions} />
      </div>

      <div className="flex gap-1 border-b border-slate-200 -mb-px">
        {[["consultores","Consultores"],["clinicas","Unidades"],["meses","Meses"],["recentes","Recentes"],["credenciados","Credenciados"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`finance-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === k ? "border-[#0B2B62] text-[#0B2B62]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {k === "credenciados" && <Award className="w-3.5 h-3.5" />}
            {l}
          </button>
        ))}
      </div>

      {tab === "consultores" && (
        <RankingTable
          testId="rank-consultores"
          rows={data.consultor_ranking}
          columns={[
            { key: "rank", label: "#", w: 1, render: (_, i) => (
              <span className="inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-amber-600" />{i + 1}</span>
            )},
            { key: "consultor_name", label: "Consultor", w: 4 },
            { key: "cases", label: "Internações", w: 2, align: "right" },
            { key: "matricula_generated", label: "Matrícula gerada", w: 2, align: "right", fmt: brl },
            { key: "commission_total", label: "Comissão paga", w: 3, align: "right", fmt: brl, strong: true },
          ]}
        />
      )}
      {tab === "clinicas" && (
        <RankingTable
          testId="rank-clinicas"
          rows={data.clinic_ranking}
          columns={[
            { key: "rank", label: "#", w: 1, render: (_, i) => (
              <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-slate-500" />{i + 1}</span>
            )},
            { key: "clinic_name", label: "Unidade", w: 4 },
            { key: "admissions", label: "Admissões", w: 2, align: "right" },
            { key: "matricula_total", label: "Matrículas", w: 2, align: "right", fmt: brl, strong: true },
            { key: "monthly_total", label: "Mensalidades", w: 3, align: "right", fmt: brl },
          ]}
        />
      )}
      {tab === "meses" && (
        <RankingTable
          testId="rank-meses"
          rows={data.monthly_series}
          columns={[
            { key: "month", label: "Mês", w: 3, render: (r) => <><Calendar className="w-3.5 h-3.5 inline mr-1 text-slate-500" />{r.month}</> },
            { key: "admissions", label: "Internações", w: 3, align: "right" },
            { key: "matricula", label: "Matrículas", w: 3, align: "right", fmt: brl, strong: true },
            { key: "commission", label: "Comissões", w: 3, align: "right", fmt: brl },
          ]}
        />
      )}
      {tab === "recentes" && (
        <RankingTable
          testId="rank-recentes"
          rows={data.recent}
          columns={[
            { key: "protocol", label: "Protocolo", w: 2, strong: true },
            { key: "patient_name", label: "Paciente", w: 3 },
            { key: "consultor_name", label: "Consultor", w: 2, render: (r) => <><User className="w-3.5 h-3.5 inline mr-1 text-slate-500" />{r.consultor_name}</> },
            { key: "clinic_name", label: "Unidade", w: 2 },
            { key: "confirmed_at", label: "Data", w: 1, render: (r) => fmt(r.confirmed_at) },
            { key: "consultor_commission", label: "Comissão", w: 2, align: "right", fmt: brl, strong: true },
          ]}
        />
      )}
      {tab === "credenciados" && <FinanceCredenciadosPanel days={days} />}
      </>}
    </div>
  );
};

const BigStat = ({ label, value, accent }) => {
  const map = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    blue: "border-blue-300 bg-blue-50 text-blue-800",
    amber: "border-amber-300 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`p-3 border rounded-lg ${map[accent] || "border-slate-200 bg-white text-[#0B2B62]"}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      <div className="font-display font-bold text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
};

const RankingTable = ({ rows, columns, testId }) => {
  if (!rows?.length) return <div className="p-6 text-sm text-slate-500 border border-dashed border-slate-300 rounded-md">Sem dados no período.</div>;
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={testId}>
      <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        {columns.map((c) => (
          <div key={c.key} className={`col-span-${c.w} ${c.align === "right" ? "text-right" : ""}`}>{c.label}</div>
        ))}
      </div>
      {rows.slice(0, 50).map((r, i) => (
        <div key={r.id || r.protocol || r.month || r.consultor_id || r.clinic_id || i} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-100 text-sm items-center">
          {columns.map((c) => {
            const raw = c.render ? c.render(r, i) : (c.fmt ? c.fmt(r[c.key]) : r[c.key]);
            return (
              <div key={c.key} className={`col-span-${c.w} ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.strong ? "font-bold text-[#0B2B62]" : "text-slate-700"} truncate`}>
                {raw ?? "—"}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default AdminFinancePanel;
